import React, { useEffect, useState } from "react";
import api, { formatApiError, formatGHS } from "@/lib/api";
import { printElement } from "@/lib/docExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export const SPLIT_METHODS = [
  { value: "Cash", label: "Cash" },
  { value: "Mobile_Money", label: "Mobile Money" },
  { value: "Card", label: "Card" },
  { value: "Bank_Transfer", label: "Bank Transfer" },
];

const selectClass = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

/** Several payment methods on one receipt (e.g. part cash, part MoMo). */
export function SplitPaymentEditor({ total, value, onChange }) {
  const rows = value || [];
  const paid = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const remaining = Math.round((Number(total || 0) - paid) * 100) / 100;
  const update = (index, patch) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  if (!rows.length) {
    return (
      <Button type="button" variant="outline" size="sm" className="w-full" data-testid="pos-split-start"
        onClick={() => onChange([{ method: "Cash", amount: "" }, { method: "Mobile_Money", amount: "" }])}>
        Split payment
      </Button>
    );
  }
  return (
    <div className="space-y-2 rounded-md border border-border p-2" data-testid="pos-split-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-mono uppercase tracking-wider">Split payment</Label>
        <button type="button" className="text-xs text-muted-foreground underline" onClick={() => onChange([])}>Cancel split</button>
      </div>
      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-[1fr_7rem_auto] gap-1">
          <select className={selectClass} value={row.method} onChange={(e) => update(index, { method: e.target.value })}
            aria-label={`Payment method ${index + 1}`}>
            {SPLIT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <Input type="number" step="0.01" min="0" className="h-9 font-mono" value={row.amount}
            placeholder={index === rows.length - 1 && remaining > 0 ? String(remaining) : "0.00"}
            onChange={(e) => update(index, { amount: e.target.value })} aria-label={`Amount ${index + 1}`} />
          <Button type="button" variant="ghost" size="sm" disabled={rows.length <= 2}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}>×</Button>
        </div>
      ))}
      <div className="flex items-center justify-between text-xs">
        <button type="button" className="underline text-muted-foreground"
          onClick={() => onChange([...rows, { method: "Card", amount: "" }])}>Add another method</button>
        <span className={`font-mono ${remaining > 0.004 ? "text-rose-600" : "text-emerald-700 dark:text-emerald-400"}`}>
          {remaining > 0.004 ? `Still to pay ${formatGHS(remaining)}` : remaining < -0.004 ? `Change ${formatGHS(-remaining)}` : "Fully paid"}
        </span>
      </div>
    </div>
  );
}

export function splitPayload(rows, total) {
  const filled = (rows || []).filter((row) => Number(row.amount || 0) > 0)
    .map((row) => ({ method: row.method, amount: Number(row.amount) }));
  if (filled.length < 2) return null;
  const paid = filled.reduce((sum, row) => sum + row.amount, 0);
  if (paid + 0.004 < Number(total || 0)) throw new Error(`Payments add up to ${formatGHS(paid)}, less than ${formatGHS(total)}`);
  return filled;
}

/** Return part or all of a receipt: restock or write off each line, refund by the original method. */
export function ReturnDialog({ sale, onClose, onDone }) {
  const [qty, setQty] = useState({});
  const [restock, setRestock] = useState({});
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setQty({}); setRestock({}); setReason(""); setMethod(""); }, [sale?.sale_id]);
  if (!sale) return null;
  const items = sale.items || [];
  const receivable = ["Credit", "Insurance"].includes(sale.payment_method);
  const lines = items.map((it, i) => ({ i, it, left: Number(it.quantity || 0) - Number(it.returned_quantity || 0) }));
  const value = lines.reduce((sum, { i, it }) => sum + Number(qty[i] || 0) * Number(it.unit_price || 0), 0);
  const submit = async () => {
    const chosen = lines.filter(({ i }) => Number(qty[i] || 0) > 0)
      .map(({ i }) => ({ line_index: i, quantity: Number(qty[i]), restock: restock[i] !== false }));
    if (!chosen.length) return toast.error("Enter a quantity to return");
    if (reason.trim().length < 3) return toast.error("Give a reason for the return");
    setBusy(true);
    try {
      const { data } = await api.post(`/pos/sales/${sale.sale_id}/returns`, {
        lines: chosen, reason: reason.trim(), refund_method: receivable ? undefined : method || undefined,
      });
      toast.success(`${data.credit_note_number}: refunded ${formatGHS(data.total)} (${String(data.refund_method).replace("_", " ")})`);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!sale} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pos-return-dialog">
        <DialogHeader><DialogTitle className="font-display">Return items from {sale.receipt_number}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-2">
            {lines.map(({ i, it, left }) => (
              <div key={i} className="grid grid-cols-[1fr_5rem_auto] items-center gap-2 border-b border-border pb-2">
                <div>
                  <p className="font-medium">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{formatGHS(it.unit_price)} each · {left} can be returned</p>
                </div>
                <Input type="number" min="0" max={left} step="1" className="h-8 font-mono" disabled={left <= 0}
                  value={qty[i] ?? ""} onChange={(e) => setQty({ ...qty, [i]: Math.min(Number(e.target.value || 0), left) || "" })}
                  aria-label={`Quantity to return of ${it.name}`} />
                {sale.sale_type !== "service" && sale.sale_type !== "hospital_service" ? (
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={restock[i] !== false} onChange={(e) => setRestock({ ...restock, [i]: e.target.checked })} />
                    Back to stock
                  </label>
                ) : <span />}
              </div>
            ))}
          </div>
          <div>
            <Label htmlFor="pos-return-reason">Reason</Label>
            <Input id="pos-return-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. wrong size, damaged" />
          </div>
          {receivable ? (
            <p className="text-xs text-muted-foreground">This was a {sale.payment_method.toLowerCase()} sale, so the refund reduces the amount the customer owes.</p>
          ) : (
            <div>
              <Label htmlFor="pos-return-method">Refund by</Label>
              <select id="pos-return-method" className={selectClass} value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="">Original payment method</option>
                {SPLIT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          )}
          <p className="text-xs text-muted-foreground">Items not put back to stock are written off. VAT, NHIL and GETFund are reversed in proportion, and a credit note number is issued.</p>
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm">About {formatGHS(value)}</span>
            <Button onClick={submit} disabled={busy} data-testid="pos-return-submit">{busy ? "Saving…" : "Issue refund"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportBody({ report }) {
  if (!report) return null;
  const rows = [
    ["Sales", report.sales_count], ["Gross sales", formatGHS(report.gross_sales)],
    ["Returns", `${report.returns_count || 0} · ${formatGHS(report.returns_total || 0)}`],
    ["Net sales", formatGHS(report.net_sales ?? report.gross_sales)],
    ["Voided", `${report.voided_count} · ${formatGHS(report.voided_total)}`],
    ["Opening float", formatGHS(report.opening_float)],
  ];
  if (!report.blind) {
    rows.push(["Cash sales", formatGHS(report.cash_sales)], ["Cash refunds", formatGHS(report.cash_refunds || 0)],
      ["Expected cash", formatGHS(report.expected_cash)]);
    if (report.counted_cash !== undefined) {
      rows.push(["Counted cash", formatGHS(report.counted_cash)], ["Over / short", formatGHS(report.cash_variance)]);
    }
  }
  return (
    <div className="space-y-1 font-mono text-xs">
      {rows.map(([label, val]) => <div key={label} className="flex justify-between"><span>{label}</span><span>{val}</span></div>)}
      <div className="border-t border-border pt-1">
        {Object.entries(report.by_payment_method || {}).map(([m, v]) => (
          <div key={m} className="flex justify-between"><span>{m.replace("_", " ")}</span><span>{formatGHS(v)}</span></div>
        ))}
      </div>
      {report.blind && <p className="pt-1 text-muted-foreground">Blind count is on: expected cash is shown after you count.</p>}
      {report.sign_off_status && <p className="pt-1">Supervisor sign-off: <b>{report.sign_off_status}</b>{report.signed_off_by_name ? ` by ${report.signed_off_by_name}` : ""}</p>}
    </div>
  );
}

/** Cashier shift: open with a float, X-report mid-shift, blind count and Z-report at close. */
export function ShiftPanel() {
  const [shift, setShift] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [float, setFloat] = useState("");
  const [count, setCount] = useState("");
  const [report, setReport] = useState(null);
  const [closing, setClosing] = useState(false);
  const reportRef = React.useRef(null);
  const load = async () => {
    try {
      const res = await api.get("/pos/shifts/current");
      const current = res && res.data && !Array.isArray(res.data) ? res.data.shift : null;
      setShift(current && typeof current === "object" ? current : null);
    } catch {
      setShift(null);
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const open = async () => {
    try {
      const { data } = await api.post("/pos/shifts/open", { opening_float: Number(float || 0) });
      setShift(data); setFloat("");
      toast.success("Shift opened");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const xReport = async () => {
    try { const { data } = await api.get(`/pos/shifts/${shift.shift_id}/x-report`); setReport(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const close = async () => {
    if (count === "") return toast.error("Count the cash in the drawer first");
    try {
      const { data } = await api.post(`/pos/shifts/${shift.shift_id}/close`, { counted_cash: Number(count) });
      setReport(data); setShift(null); setCount(""); setClosing(false);
      toast.success("Shift closed. A supervisor needs to sign it off.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!loaded) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2 text-sm" data-testid="pos-shift-panel">
      {shift ? (
        <>
          <Badge variant="outline" className="font-mono text-[10px] uppercase text-emerald-700 dark:text-emerald-400">Shift open</Badge>
          <span className="text-xs text-muted-foreground">since {String(shift.opened_at || "").slice(11, 16)} · float {formatGHS(shift.opening_float)}</span>
          <Button size="sm" variant="outline" onClick={xReport} data-testid="pos-x-report">X-report</Button>
          {closing ? (
            <>
              <Input type="number" step="0.01" min="0" className="h-8 w-32 font-mono" placeholder="Cash counted"
                value={count} onChange={(e) => setCount(e.target.value)} aria-label="Cash counted" />
              <Button size="sm" onClick={close} data-testid="pos-close-shift">Close and print Z</Button>
              <Button size="sm" variant="ghost" onClick={() => setClosing(false)}>Cancel</Button>
            </>
          ) : <Button size="sm" variant="outline" onClick={() => setClosing(true)}>Close shift</Button>}
        </>
      ) : (
        <>
          <span className="text-xs text-muted-foreground">No open shift.</span>
          <Input type="number" step="0.01" min="0" className="h-8 w-32 font-mono" placeholder="Opening float"
            value={float} onChange={(e) => setFloat(e.target.value)} aria-label="Opening float" />
          <Button size="sm" onClick={open} data-testid="pos-open-shift">Open shift</Button>
        </>
      )}
      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">{report?.report_type === "X" ? "X-report (shift still open)" : "Z-report"}</DialogTitle></DialogHeader>
          <div ref={reportRef} className="p-1"><ReportBody report={report} /></div>
          <Button variant="outline" onClick={() => printElement(`${report?.report_type || "Z"}-report`, reportRef.current, "portrait")}>Print</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Supervisor list of closed shifts waiting for sign-off. */
export function ShiftSignOffList() {
  const [shifts, setShifts] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/pos/shifts"); setShifts((data || []).filter((s) => s.status === "closed")); }
    catch { setShifts([]); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const signOff = async (shift) => {
    try {
      const { data } = await api.post(`/pos/shifts/${shift.shift_id}/sign-off`, { notes: "" });
      toast.success(data.over_short_journal_entry_id ? "Signed off; over/short posted" : "Signed off");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!shifts.length) return <p className="text-sm text-muted-foreground">No closed shifts yet.</p>;
  return (
    <div className="space-y-1 text-sm">
      {shifts.slice(0, 30).map((s) => {
        const z = s.z_report || {};
        return (
          <div key={s.shift_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-1">
            <span>{s.cashier_name} · {String(s.closed_at || "").slice(0, 16).replace("T", " ")}</span>
            <span className="font-mono text-xs">expected {formatGHS(z.expected_cash)} · counted {formatGHS(z.counted_cash)} ·
              <b className={Number(z.cash_variance || 0) < 0 ? " text-rose-600" : ""}> {formatGHS(z.cash_variance || 0)}</b></span>
            {s.sign_off_status === "approved"
              ? <Badge variant="outline" className="text-[10px] uppercase">Signed off · {s.signed_off_by_name}</Badge>
              : <Button size="sm" onClick={() => signOff(s)}>Sign off</Button>}
          </div>
        );
      })}
    </div>
  );
}

/** Returns by cashier for a date range. */
export function ReturnsSummary() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const load = async () => {
    try { const res = await api.get("/pos/returns/summary", { params: { date_from: from, date_to: to } }); setData(res.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-end gap-2">
        <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8" /></div>
        <Button size="sm" variant="outline" onClick={load}>Show</Button>
      </div>
      {data && (data.by_cashier.length ? data.by_cashier.map((c) => (
        <div key={c.cashier_id} className="flex flex-wrap justify-between gap-2 border-b border-border py-1">
          <span>{c.cashier_name}</span>
          <span className="font-mono text-xs">{c.returns} returns · {formatGHS(c.total)} · restocked {c.items_restocked} · written off {c.items_written_off}</span>
        </div>
      )) : <p className="text-muted-foreground">No returns in this period.</p>)}
    </div>
  );
}
